import * as React from 'react';
import { useEffect, useState } from 'react';
import ListMissionsItem from '@/components/ui/ListMissionsItem';
import { FrontTicketPurchaseOfferResponseDto } from '@/lib/jp-api/requests';
import ListMissionsCallout, { IMissionCalloutProps, IMissionsCalloutIntent } from '@/components/ui/ListMissionsCallout';
import Button from '@/components/ui/Button';
import { formatCurrencyWithLabel } from '@/lib/currency-formatter';
import { getDefaultCurrency } from '@/lib/currency/currency-persistence';
import { getDiscountLabel } from '@/lib/offers/get-discount-label';
import { isFutureOffer } from '@/lib/offers/is-future-offer';
import { PurchaseTicket } from '@/components/jackpot/jackpot-purchase-tickets/jackpot-purchase-tickets-client';

function getTimerText(diff: number, useShort: boolean) {
  if (diff > 0) {
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    if (useShort) {
      if (days > 0) {
        return `${days}d`;
      } else if (hours > 0) {
        return `${hours}h`;
      } else if (minutes > 0) {
        return `${minutes}m`;
      } else if (seconds > 0) {
        return `${seconds}s`;
      } else {
        return null;
      }
    } else {
      if (days > 0) {
        return `${days} days`;
      } else {
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      }
    }
  } else {
    return null;
  }
}

function getTimerIntent(diff: number): IMissionsCalloutIntent {
  if (diff > 0) {
    return diff > 24 * 60 * 60 * 1000 ? 'default' : 'danger';
  } else {
    return 'danger';
  }
}

const OfferCallout: React.FC<{ ticket: FrontTicketPurchaseOfferResponseDto }> = ({ ticket }): React.ReactNode | null => {
  const [calloutConfig, setCalloutConfig] = useState<IMissionCalloutProps | null>(null);
  useEffect(() => {
    if (!ticket.starts && !ticket.ends) return;
    const interval = setInterval(() => {
      const now = new Date();
      const offerStarts = ticket.starts ? new Date(ticket.starts) : null;
      const offerEnds = ticket.ends ? new Date(ticket.ends) : null;
      if (!offerStarts && !offerEnds) {
        setCalloutConfig(null);
      } else if (offerStarts && offerEnds) {
        if (offerEnds.getTime() < now.getTime()) {
          setCalloutConfig(null);
        } else if (offerStarts.getTime() > now.getTime()) {
          const diff = offerStarts.getTime() - now.getTime();
          setCalloutConfig({
            intent: getTimerIntent(diff),
            label: 'Unlocks in ' + getTimerText(diff, false),
            shortLabel: getTimerText(diff, true),
            icon: 'timer',
          });
        } else {
          const diff = offerEnds.getTime() - now.getTime();
          setCalloutConfig({
            intent: getTimerIntent(diff),
            label: 'Ends in ' + getTimerText(diff, false),
            shortLabel: getTimerText(diff, true),
            icon: 'timer',
          });
        }
      } else if (offerStarts) {
        if (offerStarts.getTime() > now.getTime()) {
          const diff = offerStarts.getTime() - now.getTime();
          setCalloutConfig({
            intent: getTimerIntent(diff),
            label: 'Unlocks in ' + getTimerText(diff, false),
            shortLabel: getTimerText(diff, true),
            icon: 'timer',
          });
        } else {
          setCalloutConfig(null);
        }
      } else if (offerEnds) {
        const diff = offerEnds.getTime() - now.getTime();
        setCalloutConfig({
          intent: getTimerIntent(diff),
          label: 'Ends in ' + getTimerText(diff, false),
          shortLabel: getTimerText(diff, true),
          icon: 'timer',
        });
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [ticket]);

  if (calloutConfig === null) return null;
  return (
    <ListMissionsCallout
      intent={calloutConfig.intent}
      label={calloutConfig.label}
      shortLabel={calloutConfig.shortLabel}
      icon={calloutConfig.icon}
    />
  );
};

type JackpotPurchaseTicketItemProps = {
  ticket: PurchaseTicket;
  offer: FrontTicketPurchaseOfferResponseDto;
  baseOffer: FrontTicketPurchaseOfferResponseDto;
  selected?: boolean;
  onClick?: () => void;
  onPurchaseButtonClick?: () => void;
  highlight?: string;
  buttonDescription?: string;
};

function getButtonLabel(baseOffer: FrontTicketPurchaseOfferResponseDto, offer: FrontTicketPurchaseOfferResponseDto) {
  if (offer.buttonLabel) return <span dangerouslySetInnerHTML={{ __html: offer.buttonLabel }} />;
  const baseTicketPrice = Number(baseOffer.price) / baseOffer.minimumAmount;
  if (offer.pricingModel === 'fixed') {
    const price = offer.minimumAmount * baseTicketPrice;
    const discountedPrice = Number(offer.price);
    return (
      <span>
        {formatCurrencyWithLabel(getDefaultCurrency(), Number(discountedPrice))}{' '}
        <del>{formatCurrencyWithLabel(getDefaultCurrency(), Number(price))}</del>
      </span>
    );
  } else {
    const price = offer.minimumAmount * baseTicketPrice;
    const discountedPrice = price * (1 - Number(offer.discountPercentage) / 100);
    return (
      <span>
        {formatCurrencyWithLabel(getDefaultCurrency(), Number(discountedPrice))}{' '}
        <del>{formatCurrencyWithLabel(getDefaultCurrency(), Number(price))}</del>
      </span>
    );
  }
}

export const JackpotPurchaseTicketItem = ({
  ticket,
  offer,
  baseOffer,
  selected = false,
  onClick,
  onPurchaseButtonClick,
  highlight,
  buttonDescription,
}: JackpotPurchaseTicketItemProps) => {
  return (
    <ListMissionsItem
      progress={null}
      progressLabel={null}
      selected={selected}
      onClick={onClick}
      completed={false}
      recurring={false}
      repetition={null}
      highlight={highlight}
      tickets={{
        amount: offer.minimumAmount,
        label: ticket.label,
        imageUrl: ticket.image,
      }}
      discount={getDiscountLabel(baseOffer, offer)}
      tag={offer.tag.label ? offer.tag : undefined}
      theme={ticket.theme}
      backgroundImage={offer.offerBackgroundImage}
      callout={<OfferCallout ticket={offer} />}
      button={
        <Button
          theme={offer.buttonTheme}
          title={getButtonLabel(baseOffer, offer)}
          disabled={isFutureOffer(offer)}
          onClick={onPurchaseButtonClick}
        />
      }
      buttonDescription={buttonDescription}
      layout="tickets"
    />
  );
};
